'use client';

import { useState, useEffect, useRef } from 'react';
import { X, Phone, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdvisorButtonOptional } from '@/contexts/AdvisorButtonContext';
import { trackLeadFormView, trackLeadSubmission } from '@/lib/analytics';
import { logFacebookEvent } from '@/lib/facebook';
import { useNativeApp } from '@/lib/native';
import { COUNTRY_OPTIONS, DEFAULT_COUNTRY, validatePhone } from '@/lib/phone';

interface LeadFormData {
  name: string;
  mobile: string;
  countryCode: string;
  pageUrl: string;
  service: string;
  source: string;
  description: string;
}

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const servicesByCity: Record<string, string[]> = {
  Bangalore: [
    'BBMP / Property Services',
    'Passport Services',
    'Visa Services',
    'Vehicle / RTO Services',
    'BESCOM Services',
    'Aadhaar Services',
    'PAN Services',
    'Voter ID Services',
    'Birth / Death Certificate',
    'Marriage Registration Services',
    'Ayushman Card',
    'Life Certificate',
    'My service is not listed here',
  ],
  Hyderabad: [
    'New US Visa',
    'Schengen Visa',
    'UAE Visa',
    'Kenya Visa',
    'UK Visa',
    'Australia Visa',
    'My service is not listed here',
  ],
};

const sources: Record<string, string> = {
  GOOGLE: 'Google / YouTube',
  FACEBOOK: 'Facebook / Instagram',
  LINKEDIN: 'LinkedIn',
  JUST_DIAL: 'Just Dial / Sulekha',
  QUORA_REDDIT: 'Quora / Reddit',
  REFERRAL: 'Referred by someone',
};

export default function LeadCaptureModal({
  isOpen,
  onClose,
}: LeadCaptureModalProps) {
  const { user } = useAuth();
  const advisor = useAdvisorButtonOptional();
  // Inside the native app we tag leads with channel=APP and the platform
  // (ios/android), and hide the "how did you hear about us" question.
  const { isNative, platform } = useNativeApp();
  const [step, setStep] = useState<'form' | 'thank-you'>('form');

  // Mirror the thank-you step into the advisor context so the shared
  // AppHeader can hide the city selector behind it. Gated on isOpen so it
  // resets the moment the modal closes (the internal `step` state persists).
  useEffect(() => {
    advisor?.setLeadThankYou(isOpen && step === 'thank-you');
  }, [isOpen, step, advisor]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<LeadFormData>>({});
  const [cityName, setCityName] = useState('Bangalore');
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const submittingRef = useRef(false);
  const viewTrackedRef = useRef(false);

  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    mobile: '',
    countryCode: DEFAULT_COUNTRY,
    pageUrl: '',
    service: '',
    source: '',
    description: '',
  });

  const toTitleCase = (str: string) =>
    str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
    );

  const getCookie = (name: string) => {
    if (typeof document === 'undefined') return;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
  };

  useEffect(() => {
    if (!isOpen) return;

    // Track form view only once per open (avoid re-fires when user state changes)
    if (!viewTrackedRef.current) {
      trackLeadFormView(window.location.href);
      viewTrackedRef.current = true;
    }

    setFormData((prev) => ({
      ...prev,
      pageUrl: window.location.href,
      name: user?.name || prev.name,
      mobile: user?.phone
        ? user.phone.replace(/\D/g, '').slice(-10)
        : prev.mobile,
      source: user ? 'REFERRAL' : prev.source,
    }));

    const cityCookie = getCookie('selectedCity');
    if (cityCookie) {
      setCityName(toTitleCase(decodeURIComponent(cityCookie)));
    }

    // Load wallet balance for logged-in users so they can apply it.
    if (user) {
      fetch('/api/consumer/wallet')
        .then((r) => r.json())
        .then((d) => {
          if (d?.success) setWalletBalance(d.data.balance ?? 0);
        })
        .catch(() => {});
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) {
      setStep('form');
      setFormData({
        name: '',
        mobile: '',
        countryCode: DEFAULT_COUNTRY,
        pageUrl: '',
        service: '',
        source: '',
        description: '',
      });
      setErrors({});
      setLoading(false);
      setUseWallet(false);
      setWalletBalance(0);
      submittingRef.current = false;
      viewTrackedRef.current = false;
    }
  }, [isOpen]);

  const cityServices = servicesByCity[cityName] || servicesByCity['Bangalore'];

  const handleInputChange = (field: keyof LeadFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<LeadFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // The shared rule (F-0058). The regex this replaces refused `+91 91234 56789`,
    // `919123456789` and every non-Indian number — and a refusal here means no lead at all.
    const phone = validatePhone(formData.mobile, formData.countryCode);
    if (!phone.ok) {
      newErrors.mobile = phone.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Hard guard against double submission (covers Enter-key + click + rapid taps)
    if (submittingRef.current || loading) return;
    if (!validateForm()) return;

    submittingRef.current = true;
    setLoading(true);

    try {
      const finalService = formData.description.trim()
        ? `${formData.service}. ${formData.description.trim()}`
        : formData.service;

      // Logged-in users applying wallet balance go through the authenticated
      // request path so a real request is created with the wallet amount
      // recorded (visible to ops in the console).
      if (user && useWallet && walletBalance > 0) {
        const response = await fetch('/api/consumer/requests/from-advisor', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            service: formData.service,
            description: formData.description.trim(),
            city: cityName,
            useWallet: true,
            // App leads are tagged with channel=APP + platform; web omits these.
            channel: isNative ? 'APP' : undefined,
            appPlatform: isNative ? platform : undefined,
          }),
        });
        if (!response.ok) throw new Error('Submission failed');
      } else {
        // Re-run the validator so the number we POST is the normalized local form and the
        // country code is explicit. The server does this too, but sending the resolved value
        // means the lead is stored as the customer's selection rather than as an inference.
        const phone = validatePhone(formData.mobile, formData.countryCode);
        const payload = {
          ...formData,
          mobile: phone.ok ? phone.localDigits : formData.mobile,
          countryCode: phone.ok ? phone.countryCode : formData.countryCode,
          service: finalService,
          city: cityName,
          // App leads: channel=APP + platform (ios/android). On web these are
          // undefined, so the backend keeps mapping the customer-selected
          // `source` to a channel as before.
          channel: isNative ? 'APP' : undefined,
          appPlatform: isNative ? platform : undefined,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'Direct',
          screenResolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        const apiUrl = `${process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3006'}/api/dev/leads/createEnquiryFromSite`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error('Submission failed');
      }

      trackLeadSubmission({
        service: finalService,
        source: formData.source,
      });
      // Same moment, reported to Meta so ad campaigns can be optimised and
      // audiences built against real leads rather than just installs.
      void logFacebookEvent('Lead', {
        content_name: finalService,
        source: formData.source,
        currency: 'INR',
        value: 1.0,
      });

      setStep('thank-you');
    } catch (err) {
      console.error(err);
      alert('Something went wrong. Please try again.');
      // Allow retry only after failure
      submittingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden">
        {step === 'form' && (
          <>
            <div className="bg-gradient-to-r from-primary to-primary/90 px-6 py-4 text-primary-foreground relative">
              <button
                onClick={onClose}
                className="absolute right-4 top-4 p-1 hover:bg-white/20 rounded-full cursor-pointer"
              >
                <X size={20} />
              </button>
              <h2 className="text-xl font-semibold">Connect with Advisor</h2>
              <p className="text-primary-foreground/80 text-sm mt-1">
                Get expert assistance in {cityName}
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-6 space-y-4 max-h-[70vh] overflow-y-auto"
            >
              <input
                type="text"
                placeholder="Full Name *"
                required
                disabled={!!user}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${user ? 'bg-muted text-muted-foreground' : ''} ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}

              <div className="flex gap-2">
                <select
                  value={formData.countryCode}
                  onChange={(e) => handleInputChange('countryCode', e.target.value)}
                  disabled={!!user}
                  className={`px-2 py-2 border rounded-lg ${user ? 'bg-muted text-muted-foreground' : ''}`}
                  aria-label="Country code"
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="Mobile Number *"
                  required
                  disabled={!!user}
                  value={formData.mobile}
                  // Punctuation and a pasted country code are kept, not sliced away: cutting
                  // at 10 characters mid-entry silently turns `+91 91234 56789` into a
                  // different, valid-looking number.
                  onChange={(e) => handleInputChange('mobile', e.target.value.slice(0, 20))}
                  className={`w-full px-3 py-2 border rounded-lg ${user ? 'bg-muted text-muted-foreground' : ''} ${errors.mobile ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.mobile && (
                <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
              )}

              <select
                value={formData.service}
                onChange={(e) => handleInputChange('service', e.target.value)}
                required
                className="w-full px-3 py-2 border rounded-lg bg-white"
              >
                <option value="">Select Service in {cityName}</option>
                {cityServices.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>

              <textarea
                rows={3}
                maxLength={300}
                required
                placeholder="Describe your requirement..."
                value={formData.description}
                onChange={(e) =>
                  handleInputChange('description', e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg resize-none"
              />

              {!user && !isNative && (
                <select
                  value={formData.source}
                  required
                  onChange={(e) => handleInputChange('source', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="">How did you hear about us?</option>
                  {Object.entries(sources).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              )}

              {user && walletBalance > 0 && (
                <label className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useWallet}
                    onChange={(e) => setUseWallet(e.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-primary"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Use my wallet balance (₹{walletBalance})
                  </span>
                </label>
              )}

              <button
                id="btn_advisor_form_submit"
                type="submit"
                disabled={loading}
                aria-busy={loading}
                className="w-full bg-primary cursor-pointer text-primary-foreground py-3 rounded-lg hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary inline-flex items-center justify-center gap-2"
              >
                {loading && (
                  <span
                    aria-hidden="true"
                    className="inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin"
                  />
                )}
                {loading ? 'Submitting...' : 'Connect with Advisor'}
              </button>
            </form>
          </>
        )}

        {step === 'thank-you' && (
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Thank You!
            </h2>
            <p className="text-gray-600 mb-6">
              Thank you for connecting with DocuPro. We have assigned an advisor
              to your case.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center mb-2">
                <Phone className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-medium text-blue-900">
                  Expect a call from
                </span>
              </div>
              <p className="text-lg font-semibold text-blue-700">
                +91 80 47361199
              </p>
              <p className="text-sm text-blue-600 mt-1">
                Monday - Saturday (9:00 AM - 8:00 PM)
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors duration-200"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
